<template>
  <!--
    Raw-markdown view of the live Terms of Service. Kept for back-compat
    with old "preview" links sent to counsel during the draft phase. The
    pretty-rendered version lives at /terms_and_conditions. Source file
    /legal/terms_preview.md is shared between the two.
  -->
  <div class="tos-preview-wrap">
    <div class="tos-preview-card">
      <div class="tos-preview-header">
        <h1 class="tos-preview-title">Terms of Service</h1>
        <p class="tos-preview-meta">
          Version <code>{{ tos_version }}</code>
          &middot; Effective May 3, 2026
        </p>
      </div>

      <div v-if="loading" class="tos-preview-loading">Loading&hellip;</div>
      <div v-else-if="error" class="tos-preview-error">{{ error }}</div>
      <pre v-else class="tos-preview-body">{{ markdown }}</pre>

      <p class="tos-preview-footnote">
        This page renders the Terms of Service as raw Markdown. For a
        formatted version see
        <a href="/terms_and_conditions">/terms_and_conditions</a>.
      </p>
    </div>
  </div>
</template>

<script>
import config from "@/config/constants.js";

export default {
  name: "TermsPreview",
  data() {
    return {
      markdown: "",
      loading: true,
      error: "",
      tos_version: config.tos_current_version,
    };
  },
  mounted() {
    // Markdown lives at /legal/terms_preview.md (copied from
    // src/assets/legal/ at build time). Pretty-rendered view is at
    // /terms_and_conditions; this <pre> view is kept for back-compat
    // with old preview links shared during the draft phase.
    fetch("/legal/terms_preview.md", { cache: "no-store" })
      .then((res) => {
        if (!res.ok) throw new Error("HTTP " + res.status);
        return res.text();
      })
      .then((text) => {
        this.markdown = text;
        this.loading = false;
      })
      .catch((err) => {
        console.error("[terms_preview] failed to load:", err);
        this.error = "Could not load the document.";
        this.loading = false;
      });
  },
};
</script>

<style scoped>
.tos-preview-wrap {
  min-height: 100vh;
  background: #f5f7fb;
  padding: 32px 16px;
  box-sizing: border-box;
}
.tos-preview-card {
  max-width: 920px;
  margin: 0 auto;
  background: #fff;
  border-radius: 12px;
  box-shadow: 0 4px 16px rgba(6, 29, 71, 0.06);
  padding: 36px 40px;
  box-sizing: border-box;
}
.tos-preview-header {
  border-bottom: 1px solid #e4e8f1;
  padding-bottom: 18px;
  margin-bottom: 24px;
}
.tos-preview-title {
  color: #061d47;
  font-family: "Roboto-Medium", Arial, sans-serif;
  font-size: 26px;
  font-weight: 600;
  margin: 0 0 6px;
}
.tos-preview-meta {
  color: #6a7388;
  font-size: 13px;
  margin: 0;
}
.tos-preview-meta code {
  background: #eef0f5;
  border-radius: 4px;
  padding: 1px 6px;
  font-size: 12px;
}
.tos-preview-loading,
.tos-preview-error {
  font-size: 14px;
  color: #6a7388;
  padding: 24px 0;
}
.tos-preview-error {
  color: #c0392b;
}
.tos-preview-body {
  white-space: pre-wrap;
  word-wrap: break-word;
  font-family: "SF Mono", Menlo, Monaco, Consolas, monospace;
  font-size: 13px;
  line-height: 1.55;
  color: #2d2d2d;
  background: #fafbfd;
  border: 1px solid #e4e8f1;
  border-radius: 8px;
  padding: 20px 22px;
  margin: 0;
  max-height: 70vh;
  overflow-y: auto;
}
.tos-preview-footnote {
  color: #6a7388;
  font-size: 12px;
  margin: 24px 0 0;
  line-height: 1.5;
}
.tos-preview-footnote code {
  background: #eef0f5;
  border-radius: 4px;
  padding: 1px 5px;
  font-size: 11px;
}
@media (max-width: 720px) {
  .tos-preview-card { padding: 24px 16px; }
  .tos-preview-title { font-size: 22px; }
  .tos-preview-body { padding: 14px 12px; font-size: 12px; }
}
</style>
